import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useProject } from "../../hooks/useProjects.js";
import { useRuns } from "../../hooks/useRuns.js";
import { useRunProject } from "../../hooks/useMutations.js";
import { useProfiles } from "../../hooks/useProfiles.js";
import { CheckRow } from "../../components/CheckRow/CheckRow.js";
import { RunTimeline } from "../../components/RunTimeline/RunTimeline.js";
import { Skeleton } from "../../components/Skeleton/Skeleton.js";
import { HealthBar } from "../../components/HealthBar/HealthBar.js";
import { api } from "../../lib/api.js";
import { useQueryClient } from "@tanstack/react-query";
import styles from "./ProjectDetail.module.css";

type Tab = "checks" | "history" | "config";

interface ConfigEditState {
  name: string;
  urlBase: string;
  profileSlug: string;
  schedule: string;
  active: boolean;
  auth: { enabled: boolean; username: string; password: string };
  portada: { url: string };
  categorias: { enabled: boolean; manual: string[] };
  articulos: {
    enabled: boolean;
    source: "rss" | "sitemap";
    feed_url: string;
    sitemap_url: string;
    sample: number;
  };
  amp: {
    enabled: boolean;
    pattern: "suffix" | "prefix" | "param" | "subdomain" | "ext_suffix";
    param_name: string;
    param_value: string;
    subdomain_prefix: string;
    ext_suffix: string;
  };
  technical: {
    rss_url: string;
    sitemap_url: string;
    news_sitemap_url: string;
    robots_url: string;
    ads_txt_url: string;
  };
  notify: {
    emails: string;       // comma-separated raw input
    threshold: number;
    onFail: boolean;
    onRecover: boolean;
  };
}

export function ProjectDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get("tab") as Tab | null) ?? "checks";
  const [runsPage, setRunsPage] = useState(1);
  const runProject = useRunProject();

  const { data: project, isLoading: projectLoading } = useProject(id);
  const { data: runsData, isLoading: runsLoading } = useRuns(id, runsPage);
  const { data: profiles } = useProfiles();

  // Config edit state
  const [configEdit, setConfigEdit] = useState<ConfigEditState | null>(null);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSaved, setConfigSaved] = useState(false);

  function buildInitialConfig(p: typeof project): ConfigEditState {
    const urlTypes = (p!.urlTypes as {
      portada?: { url: string };
      categorias?: { manual: string[] };
      articulos?: { source: "rss" | "sitemap"; feed_url?: string; sitemap_url?: string; sample?: number };
      amp?: { derived_from: string; pattern: "suffix" | "prefix" | "param" | "subdomain" | "ext_suffix"; param_name?: string; param_value?: string; subdomain_prefix?: string; ext_suffix?: string };
      technical?: { rss_url?: string; sitemap_url?: string; news_sitemap_url?: string; robots_url?: string; ads_txt_url?: string };
    } | null) ?? {};
    const existingAuth = (p as { auth?: { type: string; username: string; password: string } | null }).auth;
    return {
      name: p!.name,
      urlBase: p!.urlBase,
      profileSlug: p!.profileSlug,
      schedule: p!.schedule ?? "",
      active: p!.active ?? true,
      auth: {
        enabled: !!existingAuth,
        username: existingAuth?.username ?? "",
        password: existingAuth?.password ?? "",
      },
      portada: { url: urlTypes.portada?.url ?? "/" },
      categorias: {
        enabled: !!urlTypes.categorias,
        manual: urlTypes.categorias?.manual ?? [],
      },
      articulos: {
        enabled: !!urlTypes.articulos,
        source: urlTypes.articulos?.source ?? "rss",
        feed_url: urlTypes.articulos?.feed_url ?? "",
        sitemap_url: urlTypes.articulos?.sitemap_url ?? "",
        sample: urlTypes.articulos?.sample ?? 5,
      },
      amp: {
        enabled: !!urlTypes.amp,
        pattern: urlTypes.amp?.pattern ?? "suffix",
        param_name: urlTypes.amp?.param_name ?? "",
        param_value: urlTypes.amp?.param_value ?? "",
        subdomain_prefix: urlTypes.amp?.subdomain_prefix ?? "",
        ext_suffix: urlTypes.amp?.ext_suffix ?? "_amp",
      },
      technical: {
        rss_url: urlTypes.technical?.rss_url ?? "",
        sitemap_url: urlTypes.technical?.sitemap_url ?? "",
        news_sitemap_url: urlTypes.technical?.news_sitemap_url ?? "",
        robots_url: urlTypes.technical?.robots_url ?? "",
        ads_txt_url: urlTypes.technical?.ads_txt_url ?? "",
      },
      notify: (() => {
        const n = (p as { notify?: { email?: string[]; threshold?: number; on?: string[] } | null }).notify;
        return {
          emails: (n?.email ?? []).join(", "),
          threshold: n?.threshold ?? 80,
          onFail: n?.on ? n.on.includes("fail") : true,
          onRecover: n?.on ? n.on.includes("recover") : true,
        };
      })(),
    };
  }

  // Initialize configEdit when project loads and we're already on the config tab
  useEffect(() => {
    if (project && tab === "config" && !configEdit) {
      setConfigEdit(buildInitialConfig(project));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, tab]);

  const setTab = (t: Tab) => {
    setSearchParams({ tab: t });
    if (t === "config" && project && !configEdit) {
      setConfigEdit(buildInitialConfig(project));
    }
  };

  if (projectLoading) return <Skeleton height="400px" borderRadius="8px" />;
  if (!project) return <div className={styles.error}>Project not found</div>;

  const latestRun = project.latestRun;
  // Flatten all check results across all URLs for the latest run
  const checksInLatest = (latestRun?.urlResults ?? []).flatMap((ur: { url: string; urlType: string; checkResults: { slug: string; ok: boolean; value: string | null; detail: string | null; duration_ms: number }[] }) =>
    ur.checkResults.map((c) => ({ ...c, url: ur.url, urlType: ur.urlType }))
  );

  // History chart: oldest on left, newest on right
  const chartData = (runsData?.data ?? [])
    .filter((r) => r.summary)
    .map((r) => ({
      date: r.startedAt.slice(5, 16),
      score: r.summary!.score,
      id: r.id,
    }))
    .reverse();

  async function saveConfig() {
    if (!configEdit) return;
    setConfigSaving(true);
    setConfigError(null);
    setConfigSaved(false);
    try {
      const url_types: Record<string, unknown> = {
        portada: { url: configEdit.portada.url || "/" },
      };
      if (configEdit.categorias.enabled && configEdit.categorias.manual.length > 0) {
        url_types.categorias = { manual: configEdit.categorias.manual };
      }
      if (configEdit.articulos.enabled) {
        url_types.articulos = {
          source: configEdit.articulos.source,
          sample: configEdit.articulos.sample,
          ...(configEdit.articulos.source === "rss" && { feed_url: configEdit.articulos.feed_url }),
          ...(configEdit.articulos.source === "sitemap" && { sitemap_url: configEdit.articulos.sitemap_url }),
        };
      }
      if (configEdit.amp.enabled && configEdit.articulos.enabled) {
        url_types.amp = {
          derived_from: "articulos",
          pattern: configEdit.amp.pattern,
          ...(configEdit.amp.pattern === "param" && {
            param_name: configEdit.amp.param_name || "amp",
            param_value: configEdit.amp.param_value || "1",
          }),
          ...(configEdit.amp.pattern === "subdomain" && { subdomain_prefix: configEdit.amp.subdomain_prefix }),
          ...(configEdit.amp.pattern === "ext_suffix" && { ext_suffix: configEdit.amp.ext_suffix || "_amp" }),
        };
      }
      if (configEdit.technical.rss_url || configEdit.technical.sitemap_url || configEdit.technical.news_sitemap_url || configEdit.technical.robots_url || configEdit.technical.ads_txt_url) {
        url_types.technical = {
          ...(configEdit.technical.rss_url && { rss_url: configEdit.technical.rss_url }),
          ...(configEdit.technical.sitemap_url && { sitemap_url: configEdit.technical.sitemap_url }),
          ...(configEdit.technical.news_sitemap_url && { news_sitemap_url: configEdit.technical.news_sitemap_url }),
          ...(configEdit.technical.robots_url && { robots_url: configEdit.technical.robots_url }),
          ...(configEdit.technical.ads_txt_url && { ads_txt_url: configEdit.technical.ads_txt_url }),
        };
      }
      await api.put(`/projects/${id}`, {
        name: configEdit.name,
        url_base: configEdit.urlBase,
        profile: configEdit.profileSlug,
        schedule: configEdit.schedule || null,
        active: configEdit.active,
        url_types,
        auth: configEdit.auth.enabled && configEdit.auth.username && configEdit.auth.password
          ? { type: "basic", username: configEdit.auth.username, password: configEdit.auth.password }
          : null,
        notify: (() => {
          const emails = configEdit.notify.emails
            .split(",")
            .map((e) => e.trim())
            .filter((e) => e.length > 0);
          if (emails.length === 0) return null;
          const on: string[] = [];
          if (configEdit.notify.onFail) on.push("fail");
          if (configEdit.notify.onRecover) on.push("recover");
          return { email: emails, threshold: configEdit.notify.threshold, on };
        })(),
      });
      await qc.invalidateQueries({ queryKey: ["project", id] });
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 3000);
    } catch (e: unknown) {
      setConfigError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setConfigSaving(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Link to="/projects" className={styles.back}>← Projects</Link>
        <h1 className={styles.name}>{project.name}</h1>
        <code className={styles.url}>{project.urlBase}</code>
        <button
          className={styles.runBtn}
          disabled={runProject.isPending}
          onClick={() =>
            runProject.mutate(id, {
              onSuccess: (data) => navigate(`/runs/${data.runId}`),
            })
          }
        >
          {runProject.isPending ? "Running…" : "▶ Run now"}
        </button>
        {latestRun?.summary && (
          <div className={styles.health}>
            <HealthBar score={latestRun.summary.score} />
          </div>
        )}
      </div>

      <div className={styles.tabs}>
        {(["checks", "history", "config"] as Tab[]).map((t) => (
          <button
            key={t}
            className={`${styles.tab} ${tab === t ? styles.activeTab : ""}`}
            onClick={() => setTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === "checks" && (
        <div className={styles.checksPanel}>
          {checksInLatest.length === 0 ? (
            <div className={styles.empty}>No checks data for latest run.</div>
          ) : (
            checksInLatest.map((c) => (
              <CheckRow key={c.slug} check={c} />
            ))
          )}
        </div>
      )}

      {tab === "history" && (
        <div className={styles.historyPanel}>
          {runsLoading ? (
            <Skeleton height="200px" />
          ) : (
            <div className={styles.chart}>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-muted)" />
                  <XAxis dataKey="date" stroke="var(--color-text-muted)" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 100]} stroke="var(--color-text-muted)" tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ background: "var(--color-bg-elevated)", border: "1px solid var(--color-border)", borderRadius: "4px", color: "var(--color-text-primary)" }}
                  />
                  <Line type="monotone" dataKey="score" stroke="var(--color-accent)" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <RunTimeline
            runs={runsData?.data ?? []}
            hasMore={(runsData?.pagination.total ?? 0) > runsPage * 20}
            onLoadMore={() => setRunsPage((p) => p + 1)}
          />
        </div>
      )}

      {tab === "config" && (
        <div className={styles.configPanel}>
          {/* Section 1: Project info (editable) */}
          <section className={styles.configSection}>
            <h3 className={styles.configSectionTitle}>Project info</h3>

            <div className={styles.configField}>
              <label className={styles.configFieldLabel}>Name</label>
              <input
                type="text"
                className={styles.configInput}
                value={configEdit?.name ?? project.name}
                onChange={(e) => setConfigEdit((prev) => prev && ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className={styles.configField}>
              <label className={styles.configFieldLabel}>URL base</label>
              <input
                type="url"
                className={styles.configInput}
                value={configEdit?.urlBase ?? project.urlBase}
                onChange={(e) => setConfigEdit((prev) => prev && ({ ...prev, urlBase: e.target.value }))}
              />
            </div>

            <div className={styles.configField}>
              <label className={styles.configFieldLabel}>Profile</label>
              <div className={styles.configFieldHint}>Profile slug</div>
              <select
                className={styles.configSelect}
                value={configEdit?.profileSlug ?? project.profileSlug}
                onChange={(e) => setConfigEdit((prev) => prev && ({ ...prev, profileSlug: e.target.value }))}
              >
                {profiles?.map((p) => (
                  <option key={p.slug} value={p.slug}>{p.slug}</option>
                ))}
              </select>
            </div>
          </section>

          {/* Section: HTTP Basic Auth */}
          <section className={styles.configSection}>
            <h3 className={styles.configSectionTitle}>Authentication</h3>

            <div className={styles.configField}>
              <div className={styles.configToggleRow}>
                <div>
                  <div className={styles.configFieldLabel}>HTTP Basic Auth</div>
                  <div className={styles.configFieldHint}>
                    For protected environments (pre/staging). Sends <code>Authorization: Basic …</code> with every request.
                  </div>
                </div>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={configEdit.auth.enabled}
                    onChange={(e) => setConfigEdit((prev) => prev && { ...prev, auth: { ...prev.auth, enabled: e.target.checked } })}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
            </div>

            {configEdit.auth.enabled && (
              <>
                <div className={styles.configField}>
                  <label className={styles.configFieldLabel}>Username</label>
                  <input
                    className={styles.configInput}
                    type="text"
                    autoComplete="off"
                    placeholder="usuario"
                    value={configEdit.auth.username}
                    onChange={(e) => setConfigEdit((prev) => prev && { ...prev, auth: { ...prev.auth, username: e.target.value } })}
                  />
                </div>
                <div className={styles.configField}>
                  <label className={styles.configFieldLabel}>Password</label>
                  <input
                    className={styles.configInput}
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={configEdit.auth.password}
                    onChange={(e) => setConfigEdit((prev) => prev && { ...prev, auth: { ...prev.auth, password: e.target.value } })}
                  />
                </div>
              </>
            )}
          </section>

          {/* Section 2: URL types */}
          <section className={styles.configSection}>
            <h3 className={styles.configSectionTitle}>URL types</h3>

            <div className={styles.configField}>
              <label className={styles.configFieldLabel}>Portada path</label>
              <input
                type="text"
                className={styles.configInput}
                placeholder="/"
                value={configEdit?.portada.url ?? "/"}
                onChange={(e) => setConfigEdit((prev) => prev && ({ ...prev, portada: { url: e.target.value } }))}
              />
            </div>

            {/* Categorías */}
            <div className={styles.configField}>
              <div className={styles.configToggleRow}>
                <div className={styles.configFieldLabel}>Categories</div>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={configEdit?.categorias.enabled ?? false}
                    onChange={(e) => setConfigEdit((prev) => prev && ({ ...prev, categorias: { ...prev.categorias, enabled: e.target.checked } }))}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
              {configEdit?.categorias.enabled && (
                <div className={styles.configSubField}>
                  <div className={styles.configFieldHint}>One path per line</div>
                  <textarea
                    className={styles.configTextarea}
                    value={configEdit.categorias.manual.join("\n")}
                    onChange={(e) => setConfigEdit((prev) => prev && ({
                      ...prev,
                      categorias: { ...prev.categorias, manual: e.target.value.split("\n") },
                    }))}
                  />
                </div>
              )}
            </div>

            {/* Artículos */}
            <div className={styles.configField}>
              <div className={styles.configToggleRow}>
                <div className={styles.configFieldLabel}>Articles</div>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={configEdit?.articulos.enabled ?? false}
                    onChange={(e) => setConfigEdit((prev) => prev && ({ ...prev, articulos: { ...prev.articulos, enabled: e.target.checked } }))}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
              {configEdit?.articulos.enabled && (
                <div className={styles.configSubField}>
                  <select
                    className={styles.configSelect}
                    value={configEdit.articulos.source}
                    onChange={(e) => setConfigEdit((prev) => prev && ({
                      ...prev,
                      articulos: { ...prev.articulos, source: e.target.value as "rss" | "sitemap" },
                    }))}
                  >
                    <option value="rss">RSS</option>
                    <option value="sitemap">Sitemap</option>
                  </select>
                  {configEdit.articulos.source === "rss" && (
                    <input
                      type="text"
                      className={styles.configInput}
                      placeholder="rss/home.xml or full URL"
                      value={configEdit.articulos.feed_url}
                      onChange={(e) => setConfigEdit((prev) => prev && ({
                        ...prev,
                        articulos: { ...prev.articulos, feed_url: e.target.value },
                      }))}
                    />
                  )}
                  {configEdit.articulos.source === "sitemap" && (
                    <input
                      type="text"
                      className={styles.configInput}
                      placeholder="sitemap.xml or full URL"
                      value={configEdit.articulos.sitemap_url}
                      onChange={(e) => setConfigEdit((prev) => prev && ({
                        ...prev,
                        articulos: { ...prev.articulos, sitemap_url: e.target.value },
                      }))}
                    />
                  )}
                  <div className={styles.configField}>
                    <label className={styles.configFieldLabel}>Sample size</label>
                    <input
                      type="number"
                      className={styles.configInput}
                      min={1}
                      max={50}
                      value={configEdit.articulos.sample}
                      onChange={(e) => setConfigEdit((prev) => prev && ({
                        ...prev,
                        articulos: { ...prev.articulos, sample: Number(e.target.value) },
                      }))}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* AMP */}
            <div className={styles.configField}>
              <div className={styles.configToggleRow}>
                <div>
                  <div className={styles.configFieldLabel}>AMP</div>
                  {!configEdit?.articulos.enabled && (
                    <div className={styles.configFieldHint}>Requires articles</div>
                  )}
                </div>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={configEdit?.amp.enabled ?? false}
                    disabled={!configEdit?.articulos.enabled}
                    onChange={(e) => setConfigEdit((prev) => prev && ({ ...prev, amp: { ...prev.amp, enabled: e.target.checked } }))}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
              {configEdit?.amp.enabled && configEdit.articulos.enabled && (
                <div className={styles.configSubField}>
                  <select
                    className={styles.configSelect}
                    value={configEdit.amp.pattern}
                    onChange={(e) => setConfigEdit((prev) => prev && ({
                      ...prev,
                      amp: { ...prev.amp, pattern: e.target.value as ConfigEditState["amp"]["pattern"] },
                    }))}
                  >
                    <option value="suffix">Suffix — /article/amp/</option>
                    <option value="prefix">Prefix — /amp/article</option>
                    <option value="param">Param — ?outputType=amp</option>
                    <option value="subdomain">Subdomain — amp.example.com</option>
                    <option value="ext_suffix">Ext suffix — article_amp.html</option>
                  </select>
                  {configEdit.amp.pattern === "param" && (
                    <div className={styles.configSubField}>
                      <input
                        type="text"
                        className={styles.configInput}
                        placeholder="param name (e.g. outputType)"
                        value={configEdit.amp.param_name}
                        onChange={(e) => setConfigEdit((prev) => prev && ({
                          ...prev,
                          amp: { ...prev.amp, param_name: e.target.value },
                        }))}
                      />
                      <input
                        type="text"
                        className={styles.configInput}
                        placeholder="param value (e.g. amp)"
                        value={configEdit.amp.param_value}
                        onChange={(e) => setConfigEdit((prev) => prev && ({
                          ...prev,
                          amp: { ...prev.amp, param_value: e.target.value },
                        }))}
                      />
                    </div>
                  )}
                  {configEdit.amp.pattern === "subdomain" && (
                    <input
                      type="text"
                      className={styles.configInput}
                      placeholder="subdomain prefix (e.g. amp)"
                      value={configEdit.amp.subdomain_prefix}
                      onChange={(e) => setConfigEdit((prev) => prev && ({
                        ...prev,
                        amp: { ...prev.amp, subdomain_prefix: e.target.value },
                      }))}
                    />
                  )}
                  {configEdit.amp.pattern === "ext_suffix" && (
                    <input
                      type="text"
                      className={styles.configInput}
                      placeholder="suffix before extension (e.g. _amp)"
                      value={configEdit.amp.ext_suffix}
                      onChange={(e) => setConfigEdit((prev) => prev && ({
                        ...prev,
                        amp: { ...prev.amp, ext_suffix: e.target.value },
                      }))}
                    />
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Section: Technical checks config */}
          <section className={styles.configSection}>
            <h3 className={styles.configSectionTitle}>Technical checks</h3>
            <div className={styles.configFieldHint} style={{ marginBottom: "12px" }}>
              Optional URLs for checks that run on root-level files. If left empty, standard paths are used
              (<code>/robots.txt</code>, <code>/ads.txt</code>, <code>/sitemap.xml</code>).
              RSS checks will show ⚠ if no feed URL is configured.
            </div>

            <div className={styles.configField}>
              <label className={styles.configFieldLabel}>RSS feed URL</label>
              <div className={styles.configFieldHint}>
                Required for <strong>rss_valid</strong>, <strong>rss_items</strong>, <strong>rss_dates</strong>.
                Without this, those checks will show ⚠.
              </div>
              <input
                className={styles.configInput}
                placeholder="/rss.xml o https://www.example.com/rss.xml"
                value={configEdit.technical.rss_url}
                onChange={(e) =>
                  setConfigEdit((prev) => prev && { ...prev, technical: { ...prev.technical, rss_url: e.target.value } })
                }
              />
            </div>

            <div className={styles.configField}>
              <label className={styles.configFieldLabel}>Sitemap index URL</label>
              <div className={styles.configFieldHint}>
                Overrides <code>/sitemap.xml</code> for the <strong>sitemap_valid</strong> check.
              </div>
              <input
                className={styles.configInput}
                placeholder="/sitemap_index.xml (empty = /sitemap.xml)"
                value={configEdit.technical.sitemap_url}
                onChange={(e) =>
                  setConfigEdit((prev) => prev && { ...prev, technical: { ...prev.technical, sitemap_url: e.target.value } })
                }
              />
            </div>

            <div className={styles.configField}>
              <label className={styles.configFieldLabel}>Google News sitemap URL</label>
              <div className={styles.configFieldHint}>
                Required for <strong>google_news_name</strong> and <strong>google_news_freshness</strong> checks. Without this field those checks show ⚠.
              </div>
              <input
                className={styles.configInput}
                placeholder="/news-sitemap.xml or https://www.example.com/news-sitemap.xml"
                value={configEdit.technical.news_sitemap_url}
                onChange={(e) =>
                  setConfigEdit((prev) => prev && { ...prev, technical: { ...prev.technical, news_sitemap_url: e.target.value } })
                }
              />
            </div>

            <div className={styles.configField}>
              <label className={styles.configFieldLabel}>Robots.txt URL</label>
              <div className={styles.configFieldHint}>
                Overrides <code>/robots.txt</code> for the <strong>robots_txt</strong> check.
              </div>
              <input
                className={styles.configInput}
                placeholder="/robots.txt (empty = standard path)"
                value={configEdit.technical.robots_url}
                onChange={(e) =>
                  setConfigEdit((prev) => prev && { ...prev, technical: { ...prev.technical, robots_url: e.target.value } })
                }
              />
            </div>

            <div className={styles.configField}>
              <label className={styles.configFieldLabel}>Ads.txt URL</label>
              <div className={styles.configFieldHint}>
                Overrides <code>/ads.txt</code> for the <strong>ads_txt</strong> check.
              </div>
              <input
                className={styles.configInput}
                placeholder="/ads.txt (empty = standard path)"
                value={configEdit.technical.ads_txt_url}
                onChange={(e) =>
                  setConfigEdit((prev) => prev && { ...prev, technical: { ...prev.technical, ads_txt_url: e.target.value } })
                }
              />
            </div>
          </section>

          {/* Section 3: Monitoring */}
          <section className={styles.configSection}>
            <h3 className={styles.configSectionTitle}>Monitoring</h3>

            <div className={styles.configField}>
              <div className={styles.configToggleRow}>
                <div>
                  <div className={styles.configFieldLabel}>Project active</div>
                  <div className={styles.configFieldHint}>
                    When off, this project is paused — excluded from dashboard stats, failure reports and scheduled runs.
                  </div>
                </div>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={configEdit?.active ?? project.active ?? true}
                    onChange={(e) => setConfigEdit((prev) => prev && ({ ...prev, active: e.target.checked }))}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
            </div>

            <div className={styles.configField}>
              <div className={styles.configFieldLabel}>Scheduled runs (cron)</div>
              <div className={styles.configFieldHint}>
                Standard 5-field cron expression. Leave empty to disable automatic runs (you can still run manually).
              </div>
              <input
                type="text"
                className={styles.configInput}
                placeholder="e.g. 0 */6 * * *"
                value={configEdit?.schedule ?? project.schedule ?? ""}
                onChange={(e) => setConfigEdit((prev) => prev && ({ ...prev, schedule: e.target.value }))}
              />
              <div className={styles.configCronPresets}>
                {[
                  { label: "Every hour", value: "0 * * * *" },
                  { label: "Every 6h", value: "0 */6 * * *" },
                  { label: "Daily", value: "0 8 * * *" },
                  { label: "Disable", value: "" },
                ].map((p) => (
                  <button
                    key={p.label}
                    className={styles.presetBtn}
                    onClick={() => setConfigEdit((prev) => prev && ({ ...prev, schedule: p.value }))}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Section: Email notifications */}
          <section className={styles.configSection}>
            <h3 className={styles.configSectionTitle}>Email notifications</h3>
            <div className={styles.configFieldHint} style={{ marginBottom: "12px" }}>
              Send automatic alerts when the pass rate crosses the configured threshold.
              Requires SMTP configured in the server environment (<code>SMTP_HOST</code>, etc.).
            </div>

            <div className={styles.configField}>
              <label className={styles.configFieldLabel}>Recipients</label>
              <div className={styles.configFieldHint}>Comma-separated email addresses.</div>
              <input
                type="text"
                className={styles.configInput}
                placeholder="ops@company.com, qa@company.com"
                value={configEdit.notify.emails}
                onChange={(e) =>
                  setConfigEdit((prev) => prev && { ...prev, notify: { ...prev.notify, emails: e.target.value } })
                }
              />
            </div>

            <div className={styles.configField}>
              <label className={styles.configFieldLabel}>Alert threshold (%)</label>
              <div className={styles.configFieldHint}>
                Send an alert when pass rate drops below this value. Default: 80%.
              </div>
              <input
                type="number"
                min={0}
                max={100}
                className={styles.configInput}
                style={{ width: "120px" }}
                value={configEdit.notify.threshold}
                onChange={(e) =>
                  setConfigEdit((prev) => prev && { ...prev, notify: { ...prev.notify, threshold: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) } })
                }
              />
            </div>

            <div className={styles.configField}>
              <label className={styles.configFieldLabel}>Notify on</label>
              <div style={{ display: "flex", gap: "20px", marginTop: "6px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "0.875rem" }}>
                  <input
                    type="checkbox"
                    checked={configEdit.notify.onFail}
                    onChange={(e) =>
                      setConfigEdit((prev) => prev && { ...prev, notify: { ...prev.notify, onFail: e.target.checked } })
                    }
                  />
                  <span>🚨 Below threshold</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "0.875rem" }}>
                  <input
                    type="checkbox"
                    checked={configEdit.notify.onRecover}
                    onChange={(e) =>
                      setConfigEdit((prev) => prev && { ...prev, notify: { ...prev.notify, onRecover: e.target.checked } })
                    }
                  />
                  <span>✅ Recovery</span>
                </label>
              </div>
            </div>
          </section>

          {configError && <div className={styles.configError}>{configError}</div>}
          {configSaved && <div className={styles.configSuccess}>✓ Saved successfully</div>}
          <button className={styles.saveBtn} disabled={configSaving} onClick={saveConfig}>
            {configSaving ? "Saving…" : "Save changes"}
          </button>
        </div>
      )}
    </div>
  );
}
